// frontend/src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useUser, useAuth as useClerkAuth, useClerk } from '@clerk/clerk-react';
import api, { setTokenResolver, SESSION_EXPIRED_EVENT } from '../services/api';

const AuthContext = createContext({});

// What the shell can show (wf-shell-state 5–8, YES-262):
//   loading     Clerk or /api/auth/me not answered yet
//   signedOut   no Clerk session
//   ready       /me answered; role and permissions known
//   expired     /me or a later call returned 401 (session expired, or a token from another Clerk instance)
//   unreachable /me failed without an answer or with any other error; the shell never guesses a role
export const AuthProvider = ({ children }) => {
    const { user: clerkUser, isLoaded: isClerkLoaded, isSignedIn } = useUser();
    const { getToken } = useClerkAuth();
    const { signOut } = useClerk();
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [failure, setFailure] = useState(null);
    const [attempt, setAttempt] = useState(0);

    // Provide the getToken function to the generic API interceptor globally
    useEffect(() => {
        if (getToken) {
            setTokenResolver(() => getToken());
        }
    }, [getToken]);

    // Any call that comes back 401 ends the session as far as the shell is concerned
    useEffect(() => {
        const onExpired = () => setFailure('expired');
        window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
        return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
    }, []);

    useEffect(() => {
        let isMounted = true;
        const fetchProfile = async () => {
            if (isClerkLoaded) {
                if (isSignedIn && clerkUser) {
                    try {
                        const token = await getToken();
                        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

                        const response = await api.get('/api/auth/me');
                        if (isMounted) {
                            setUserProfile(response.data.user);
                            setFailure(null);
                        }
                    } catch (err) {
                        if (isMounted) {
                            setError(err.response?.data?.message || 'Failed to sync profile');
                            setUserProfile(null);
                            setFailure(err.response?.status === 401 ? 'expired' : 'unreachable');
                        }
                    }
                } else {
                    if (isMounted) {
                        setUserProfile(null);
                        setFailure(null);
                    }
                    delete api.defaults.headers.common['Authorization'];
                }
                if (isMounted) setLoading(false);
            }
        };

        fetchProfile();

        return () => {
            isMounted = false;
        };
    }, [isClerkLoaded, isSignedIn, clerkUser, getToken, attempt]);

    // Try again on "Can't reach ADAS right now" repeats /api/auth/me
    const retry = useCallback(() => {
        setLoading(true);
        setFailure(null);
        setAttempt((n) => n + 1);
    }, []);

    // options go to Clerk's signOut, e.g. { redirectUrl: '/account-deleted' }, except `cause`:
    // why the session ends ('signed_out' or 'idle'), recorded before signing out for Admin ▸ Activity
    // (YES-314); null when there is no session left to end (expired, account deleted). A failed
    // call never blocks the sign-out: the backend then closes the session at its last activity.
    const logout = async ({ cause = 'signed_out', ...options } = {}) => {
        if (cause) {
            try {
                await api.post('/api/sessions/end', { cause }, { timeout: 3000 });
            } catch (err) {
                // signing out matters more than the record
            }
        }
        return signOut(options);
    };

    const updateProfile = async (data) => {
        try {
            // { school, state, country } at the top level (docs/openapi.yaml, PUT /api/users/profile)
            await api.put('/api/users/profile', data);
            setUserProfile(prevUser => ({
                ...prevUser,
                profile: { ...prevUser?.profile, ...data }
            }));
            return data;
        } catch (error) {
            setError(error.response?.data?.message || 'Profile update failed');
            throw error;
        }
    };

    // Composite user object merging Clerk fields with the DB profile. Role and
    // permissions come from the backend (GET /api/auth/me), which enforces them;
    // Clerk's publicMetadata.role can lag behind (YES-196).
    const permissions = useMemo(() => userProfile?.permissions || [], [userProfile]);
    const mergedUser = clerkUser ? {
        ...clerkUser,
        role: userProfile?.role,
        permissions,
        dbProfile: userProfile?.profile
    } : null;

    const can = useCallback((permission) => permissions.includes(permission), [permissions]);

    // Right after sign-in, Clerk says signed in before /me has answered: still loading, so no
    // page routes on an empty permission list
    const isLoading = !isClerkLoaded || loading || Boolean(isSignedIn && !userProfile && !failure);
    let status = 'ready';
    if (isClerkLoaded && !isSignedIn) status = 'signedOut';
    else if (failure) status = failure;
    else if (isLoading) status = 'loading';

    return (
        <AuthContext.Provider
            value={{
                user: mergedUser,
                loading: isLoading,
                error,
                status,
                isAuthenticated: isSignedIn,
                can,
                retry,
                logout,
                updateProfile
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
